import type { Action } from '@evisort/eds/types/Action';
import type { IntlShape } from 'react-intl';

import chunk from 'lodash/chunk';
import Papa from 'papaparse';

import type {
  ClientFieldResource,
  FieldSectionRelatedResource,
  FieldSectionResource,
} from '~/api/unified-fields/typedefs.autogen';
import type { K2ReferenceEntityKey } from '~/features/unified-fields/k2ReferenceEntities';
import type { UfFieldValue } from '~/features/unified-fields/questionInputRegistry';
import type { TextInputType } from '~/features/unified-fields/textInputSettings';
import type { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import type {
  ClientField,
  CreateFieldOptionsApiArg,
  CreateFieldOptionsApiResponse,
  FieldOption,
  LogicalType,
} from '~/redux/api/unified-fields/enhancements';

import {
  formatStoredNumber,
  type NumberDisplaySettings,
  type NumberFormat,
} from '~/features/unified-fields/numberDisplaySettings';
import { formatDuration } from '~/features/unified-fields/read-components/DurationReadComponent';
import { formatAddress } from '~/features/unified-fields/read-components/formatAddress';
import { formatMonetaryFieldValue } from '~/features/unified-fields/ufValueFormatters';

type FieldSectionType = 'SYSTEM' | 'USER';

export type FieldSection = (
  | FieldSectionResource
  | FieldSectionRelatedResource
) & {
  attributes: {
    section_type?: FieldSectionType;
  };
};

export type FieldSectionSearchResult = {
  section: FieldSection;
  matchingFieldIds?: string[];
};

export type FieldStatusFilter = 'active' | 'inactive';

const DEFAULT_FIELD_VERSION = 1;

export const FIELD_DESCRIPTION_MAX_LENGTH = 2000;

// Shared left-column width for every row-style field label in the Add/Edit
// Field forms (Field Name, Section, Field Type, Internal API ID, Description,
// Options' Entries/Answer entry/Set default, Advanced Settings' Document
// Types/Show on upload, Edit's Used in Workflows) -- wide enough to fit the
// longest of them ("Used in Workflows", the widest) on one line.
// NameField/SectionField/TypeField use EDS 2.0 FormField's own `layout="row"`
// mode, whose default label width (the `--eds--size-96` token, 96px -- too
// narrow for these labels) AddFieldFormContent/EditFieldFormContent override
// to this same value via a CSS custom property scoped to the form, so every
// row lines up without changing that token's default anywhere else FormField
// is used. The remaining rows hand-roll their own matching column instead of
// going through FormField, so they just read this constant directly.
export const LABEL_COLUMN_WIDTH = 128;

/** Coerces optimistic-lock version from a resource's meta for PATCH/DELETE requests. */
export const getFieldVersion = (field: {
  meta?: Record<string, unknown> | null;
}): number => {
  const rawVersion = field.meta?.version;
  if (rawVersion == null) {
    return DEFAULT_FIELD_VERSION;
  }

  const version = Number(rawVersion);
  if (!Number.isInteger(version) || version < DEFAULT_FIELD_VERSION) {
    return DEFAULT_FIELD_VERSION;
  }

  return version;
};

/** Same coercion as `getFieldVersion`, named for readability at section call sites. */
export const getSectionVersion = getFieldVersion;

/** getFieldSection's query args — shared so callers hit the same cache entry. */
export const getFieldSectionCacheArg = (sectionId: string) => ({
  sectionId,
  include: 'client_fields' as const,
});

/**
 * Resolves a section reorder from a full before/after id order — the shape the
 * list column's drag-end callback reports, which names only the final order
 * rather than an explicit active/over pair. Finds the single id whose removal
 * makes both orders agree on everything else (the one that moved), then
 * rebuilds the reordered list and that section.
 */
export const resolveSectionMoveFromOrder = (
  sections: FieldSection[],
  orderedIds: string[],
): {
  reordered: FieldSection[];
  movedSection: FieldSection;
} | null => {
  const byId = new Map(sections.map((section) => [section.id, section]));
  const oldOrder = sections.map((section) => section.id);
  if (oldOrder.length !== orderedIds.length) return null;

  const movedId = oldOrder.find((id) => {
    const oldWithout = oldOrder.filter((otherId) => otherId !== id);
    const newWithout = orderedIds.filter((otherId) => otherId !== id);
    return oldWithout.every((value, index) => value === newWithout[index]);
  });
  if (!movedId) return null;

  const movedSection = byId.get(movedId);
  if (!movedSection) return null;

  const reordered = orderedIds.flatMap((id) => {
    const section = byId.get(id);
    return section ? [section] : [];
  });

  return { reordered, movedSection };
};

/** Resolves the latest field record from a list by id (e.g. after cache refetch). */
export const resolveFieldById = (
  fields: ClientField[],
  fieldId: string | null,
): ClientField | null =>
  fieldId ? (fields.find((field) => field.id === fieldId) ?? null) : null;

/**
 * Finds an unused default section name, starting the guess at
 * `existingNames.length + 1` and incrementing past any collision. A guess
 * based on count alone can repeat a name still in use by another section —
 * e.g. delete "Section 1" from a two-section list, then add a new section:
 * count-based naming would re-mint "Section 2", which is already taken.
 */
export const getNextAvailableSectionName = (
  existingNames: string[],
  formatName: (sectionNumber: number) => string,
): string => {
  const taken = new Set(existingNames);
  let sectionNumber = existingNames.length + 1;
  let name = formatName(sectionNumber);
  while (taken.has(name)) {
    sectionNumber += 1;
    name = formatName(sectionNumber);
  }
  return name;
};

/**
 * Field identifiers assigned to a section, from the `client_fields`
 * relationship on a `?include=client_fields` list response. Only
 * `FieldSectionRelatedResource` carries `relationships`; a plain
 * `FieldSectionResource` (no include) has none.
 */
export const getSectionFieldRefs = (section: FieldSection) => {
  if (!('relationships' in section)) return [];

  const data = section.relationships.client_fields?.data;
  if (data == null) return [];
  return Array.isArray(data) ? data : [data];
};

export const getSectionFieldCount = (section: FieldSection): number =>
  getSectionFieldRefs(section).length;

export const isDefaultFieldSection = (section: FieldSection): boolean =>
  Boolean(section.attributes.is_default);

export const isSystemFieldSection = (section: FieldSection): boolean =>
  section.attributes.section_type === 'SYSTEM';

export const resolveDeleteDestinationSectionId = (
  sections: FieldSection[],
  deletingSectionId: string,
): string | null => {
  const destinations = sections.filter(
    (section) => section.id !== deletingSectionId,
  );
  const defaultDestination = destinations.find(isDefaultFieldSection);
  return defaultDestination?.id ?? destinations[0]?.id ?? null;
};

export const searchFieldSections = ({
  sections,
  fields,
  query,
  status,
}: {
  sections: FieldSection[];
  fields: ClientFieldResource[];
  query: string;
  status?: FieldStatusFilter | null;
}): FieldSectionSearchResult[] => {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  const matchesQuery = (value: string | null | undefined) =>
    value?.toLocaleLowerCase().includes(normalizedQuery) ?? false;

  return sections.flatMap((section) => {
    const sectionMatchesQuery = matchesQuery(section.attributes.name);
    if (!status && sectionMatchesQuery) return [{ section }];

    const matchingFieldIds = getSectionFieldRefs(section).flatMap(
      ({ id: fieldId }) => {
        const field = fieldsById.get(fieldId);
        if (!field) return [];

        const isInactive = Boolean(field.attributes.deactivated_at);
        if (status === 'active' && isInactive) return [];
        if (status === 'inactive' && !isInactive) return [];

        const { api_name: apiName, custom_name: customName } = field.attributes;
        return sectionMatchesQuery ||
          matchesQuery(customName) ||
          matchesQuery(apiName)
          ? [fieldId]
          : [];
      },
    );

    return matchingFieldIds.length > 0 ? [{ section, matchingFieldIds }] : [];
  });
};

/**
 * Smart fields are integration-managed fields owned by Evisort itself (as
 * opposed to a third-party integration). Unlike other integration-managed
 * fields, they can be edited — except their name, which the integration owns.
 */
export const isSmartField = (field: ClientField): boolean =>
  field.attributes.integration_field_mappings.some(
    (mapping) => mapping.namespace === 'evisort',
  );

/**
 * Mapping domains that get a source chip, in the lowercase form we compare
 * against. This is the single source of truth for which domains are recognized:
 * `FieldSourceChip` keys a total `Record` off the exported `FieldSourceDomain`,
 * so adding a domain here won't compile until it also has a label and color.
 */
const FIELD_SOURCE_DOMAINS = ['standardai', 'customai'] as const;

export type FieldSourceDomain = (typeof FIELD_SOURCE_DOMAINS)[number];

export const FIELD_SOURCE_DOMAIN_STANDARD_AI = FIELD_SOURCE_DOMAINS[0];
export const FIELD_SOURCE_DOMAIN_CUSTOM_AI = FIELD_SOURCE_DOMAINS[1];

const isFieldSourceDomain = (
  domain: string | undefined,
): domain is FieldSourceDomain =>
  FIELD_SOURCE_DOMAINS.some((candidate) => candidate === domain);

/**
 * Structural shape common to `ClientField` and the autogen `ClientFieldResource`.
 * The two field-manager tabs are typed against different resources, and the
 * autogen one leaves `integration_field_mappings` optional.
 */
export type FieldWithIntegrationMappings = {
  attributes: {
    integration_field_mappings?: { domain?: string | null }[];
  };
};

/**
 * The service treats `domain` as free-form and never normalizes it, so compare
 * case-insensitively. The AI domains are mutually exclusive on a field, so the
 * first mapping that names one wins.
 */
export const getFieldSourceDomain = (
  field: FieldWithIntegrationMappings,
): FieldSourceDomain | null => {
  for (const mapping of field.attributes.integration_field_mappings ?? []) {
    const domain = mapping.domain?.toLowerCase();
    if (isFieldSourceDomain(domain)) return domain;
  }
  return null;
};

/**
 * Field types added in the M2 milestone. Creating them only needs
 * FlagType.UnifiedFieldsM1 (see AddFieldPanel.tsx's type dropdown filter) —
 * unlike the M2 gate on value-shape features below, which applies to every
 * type except TEXT (see `requiresM2ForValueFeatures`).
 */
export const M2_NEW_FIELD_TYPES = new Set([
  'EMAIL',
  'URL',
  'DURATION',
  'DEPARTMENT',
  'ADDRESS',
]);

/**
 * Whether a type's Preset options/option-set editor and Allow multiple
 * values require `isM2ValueFeaturesEnabled` (FlagType.UnifiedFieldsM2) —
 * every type except TEXT, which has shipped both unconditionally since
 * before M2 existed and stays that way.
 */
const requiresM2ForValueFeatures = (
  logicalType: string | null | undefined,
): boolean => logicalType !== 'TEXT';

/**
 * Field types that can hold more than one value, and so expose the
 * top-level "Allow multiple values" selection (AdvancedSettings'
 * `supportsSelectionMode`) — independent of whether the type also supports
 * an option set (`supportsOptionSetEditor`). Boolean stays single-value (a
 * yes/no field can't be multiple); User/Department/File are multi-value but
 * have no curated option list; Reference is out of scope (EKP-59205).
 *
 * Requires `isM2ValueFeaturesEnabled` for every type except TEXT — see
 * `requiresM2ForValueFeatures`.
 */
export const supportsMultipleValues = (
  logicalType: string | null | undefined,
  isM2ValueFeaturesEnabled = true,
): boolean => {
  if (requiresM2ForValueFeatures(logicalType) && !isM2ValueFeaturesEnabled) {
    return false;
  }
  return (
    logicalType === 'TEXT' ||
    logicalType === 'FILE' ||
    logicalType === 'USER' ||
    logicalType === 'NUMBER' ||
    logicalType === 'DATE' ||
    logicalType === 'MONETARY' ||
    logicalType === 'EMAIL' ||
    logicalType === 'URL' ||
    logicalType === 'DEPARTMENT' ||
    logicalType === 'ADDRESS' ||
    logicalType === 'DURATION'
  );
};

/**
 * TEXT, EMAIL, NUMBER, DATE, URL, MONETARY, ADDRESS, and DURATION fields may
 * attach predefined values via an option set in the field manager.
 *
 * Requires `isM2ValueFeaturesEnabled` for every type except TEXT — see
 * `requiresM2ForValueFeatures`.
 */
export const supportsOptionSetEditor = (
  logicalType: string | null | undefined,
  supportsOptionSet = true,
  isM2ValueFeaturesEnabled = true,
): boolean => {
  if (requiresM2ForValueFeatures(logicalType) && !isM2ValueFeaturesEnabled) {
    return false;
  }
  return (
    (logicalType === 'TEXT' ||
      logicalType === 'EMAIL' ||
      logicalType === 'NUMBER' ||
      logicalType === 'DATE' ||
      logicalType === 'URL' ||
      logicalType === 'MONETARY' ||
      logicalType === 'ADDRESS' ||
      logicalType === 'DURATION') &&
    supportsOptionSet
  );
};

/**
 * TEXT/EMAIL/NUMBER/DATE options are single strings/scalars that fit the
 * existing single-column CSV pipeline (parseCsvFile / deduplicateCsvLabels /
 * the template download). Compound types (URL, MONETARY, and eventually
 * ADDRESS/DURATION) need a multi-column redesign, tracked separately
 * (EKP-59379) — CSV import stays off for them until then, even though they
 * now support the option-set editor itself.
 */
export const supportsCsvImport = (
  logicalType: string | null | undefined,
): boolean =>
  logicalType === 'TEXT' ||
  logicalType === 'EMAIL' ||
  logicalType === 'NUMBER' ||
  logicalType === 'DATE';

/** "Free-form entry" (no option set) vs "Preset options" (option set) — the choice AdvancedSettings exposes for option-set-capable fields. */
export type ValueMode = 'free-form' | 'preset';

/**
 * An option-set-capable field left on Free-form entry never gets an option set
 * or its related settings (`allowAddingNewOptions`), no matter what's left
 * over in form state from a prior visit to Preset options — AdvancedSettings
 * intentionally preserves that state across mode switches so a user doesn't
 * lose their work. `allowMultipleValues` is passed through unchanged: since
 * EKP-59142, "Allow multiple values" is a top-level setting independent of
 * the option-set editor, so a free-form field can still be multi-value.
 */
export const resolveValueModeSettings = (
  logicalType: string | null | undefined,
  valueMode: ValueMode,
  allowMultipleValues: boolean,
  allowAddingNewOptions: boolean,
  isM2ValueFeaturesEnabled = true,
) => {
  const isFreeForm =
    supportsOptionSetEditor(logicalType, true, isM2ValueFeaturesEnabled) &&
    valueMode === 'free-form';
  return {
    isFreeForm,
    allowMultipleValues,
    allowAddingNewOptions: !isFreeForm && allowAddingNewOptions,
  };
};

/**
 * Preset options with nothing configured — no values and no way to get any
 * later — would leave a picklist that can never have any options (or, for a
 * field that already has an option set attached, is already handled by the
 * auto-delete-when-empty logic on submit instead of needing to block here).
 */
export const isIncompletePresetField = ({
  valueMode,
  supportsOptionSet,
  hasExistingOptionSet,
  hasValues,
  allowAddingNewOptions,
}: {
  valueMode: ValueMode;
  supportsOptionSet: boolean;
  hasExistingOptionSet: boolean;
  hasValues: boolean;
  allowAddingNewOptions: boolean;
}): boolean =>
  valueMode === 'preset' &&
  !hasExistingOptionSet &&
  supportsOptionSet &&
  !hasValues &&
  !allowAddingNewOptions;

/** TEXT, NUMBER, DATE, MONETARY, EMAIL, URL, ADDRESS, and DURATION fields support the allow_adding_new_options setting. */
export const supportsAllowAddingNewOptions = (
  logicalType: string | null | undefined,
): boolean =>
  logicalType === 'TEXT' ||
  logicalType === 'NUMBER' ||
  logicalType === 'DATE' ||
  logicalType === 'MONETARY' ||
  logicalType === 'EMAIL' ||
  logicalType === 'URL' ||
  logicalType === 'ADDRESS' ||
  logicalType === 'DURATION';

/** Only REFERENCE fields expose the "which entity does this search" picker. */
export const supportsReferenceEntity = (
  logicalType: string | null | undefined,
): boolean => logicalType === 'REFERENCE';

export const buildCustomSettingsAttributes = (
  logicalType: string | null | undefined,
  allowMultipleValues: boolean,
  allowAddingNewOptions = false,
  referenceEntity?: K2ReferenceEntityKey,
  numberFormat?: NumberFormat,
  inputType?: TextInputType,
) => {
  const settings: Record<string, boolean | string> = {};

  // `isM2ValueFeaturesEnabled: true` here is a payload-shape check, not UI
  // gating: this must reflect the type's true capability regardless of
  // FlagType.UnifiedFieldsM2, so allow_multiple_values is always sent
  // explicitly below rather than left to the backend's per-type default.
  if (supportsMultipleValues(logicalType, true)) {
    // Always sent explicitly (never omitted) — Unified Fields defaults
    // allow_multiple_values to `true` for some of these types (e.g.
    // EMAIL/URL/DEPARTMENT) when the key is omitted, which would silently
    // create a multi-value field the toggle shows as single. Sending the
    // toggle's actual value (false by default on create) overrides that.
    settings.allow_multiple_values = allowMultipleValues;
  }

  if (supportsAllowAddingNewOptions(logicalType)) {
    settings.allow_adding_new_options = allowAddingNewOptions;
  }

  if (supportsReferenceEntity(logicalType) && referenceEntity) {
    settings.reference_entity = referenceEntity;
  }

  if (logicalType === 'NUMBER' && numberFormat) {
    settings.number_format = numberFormat;
  }

  if (logicalType === 'TEXT' && inputType) {
    settings.input_type = inputType;
  }

  return Object.keys(settings).length > 0 ? { custom_settings: settings } : {};
};

/**
 * An option's value: a plain string for scalar types (TEXT/EMAIL/NUMBER/
 * DATE), or the compound wire-shape object for compound types (URL:
 * `{value, label}`; MONETARY: `{amount, currency}`). Always already in wire
 * shape — never a widget's local editing state — so it can go straight into
 * a `field_options` create payload.
 */
export type OptionDraftValue = string | Record<string, unknown>;

/**
 * Whether a draft option value counts as "nothing entered yet" — true for a
 * blank/whitespace-only string (covers both a genuinely-empty scalar input
 * and `''`, the sentinel OptionValueInput emits for an incomplete compound
 * value), false for any populated string or compound object.
 */
export const isEmptyOptionDraftValue = (value: OptionDraftValue): boolean =>
  typeof value === 'string' ? !value.trim() : false;

/** Trims a scalar draft value before staging/saving; compound values pass through unchanged. */
export const trimOptionDraftValue = (
  value: OptionDraftValue,
): OptionDraftValue => (typeof value === 'string' ? value.trim() : value);

/**
 * Converts a persisted `field_options.value` (always an object on the wire,
 * even for scalar types — `{"value": "Active"}`) into the `OptionDraftValue`
 * shape the rest of the option-editor pipeline uses: a bare string for
 * scalar types (matching what a fresh add/edit produces), or the object
 * unchanged for compound types (URL, MONETARY, ADDRESS, DURATION — there's
 * no unwrapped form for these, the stored object already *is* the draft
 * shape). Inverse of `createFieldOptionsInChunks`'s own
 * `{value} -> {value: {value}}` wrapping.
 */
export const parseOptionDraftValue = (
  logicalType: string | null | undefined,
  storedValue: Record<string, unknown>,
): OptionDraftValue =>
  logicalType === 'MONETARY' ||
  logicalType === 'URL' ||
  logicalType === 'ADDRESS' ||
  logicalType === 'DURATION'
    ? storedValue
    : String(storedValue.value ?? '');

/**
 * Human-readable display text for an option value — used for the option
 * list row, search, and (via `getFieldOptionLabel`) existing persisted
 * options. Mirrors the backend's own per-type `compute_display_text` so the
 * admin UI stays consistent with it.
 *
 * `formatNumber` (from `useFormatNumber`) drives MONETARY's locale-aware
 * currency formatting via `formatMonetaryFieldValue`, and `intl` drives
 * DURATION's pluralized "N days"/"N weeks" formatting via `formatDuration`
 * — the same formatters the document read components use, so an option
 * reads the same way here as it does everywhere else that value is
 * displayed. Callers that only need a stable label for matching/tracking
 * (default-option bookkeeping, not anything shown to a user) can omit
 * either — MONETARY/DURATION then fall back to a plain, unformatted
 * `"amount currency"`/`"amount period"` string.
 */
export const getOptionDisplayText = (
  logicalType: string | null | undefined,
  value: OptionDraftValue | null | undefined,
  formatNumber?: ReturnType<typeof useFormatNumber>,
  intl?: IntlShape,
  numberDisplay?: NumberDisplaySettings,
): string => {
  if (value == null) return '';
  if (logicalType === 'NUMBER') {
    const raw =
      typeof value === 'string' ? value : (value as { value?: unknown }).value;
    if (numberDisplay?.numberFormat === 'PERCENT') {
      return formatStoredNumber(raw, numberDisplay) ?? '';
    }
    return String(raw ?? '');
  }
  if (typeof value === 'string') return value;
  if (logicalType === 'MONETARY') {
    const { amount, currency } = value as {
      amount?: unknown;
      currency?: unknown;
    };
    if (amount == null || currency == null) return '';
    if (!formatNumber) return `${amount} ${currency}`;
    return formatMonetaryFieldValue(
      [{ amount, currency } as UfFieldValue],
      formatNumber,
    );
  }
  if (logicalType === 'URL') {
    const { value: url, label } = value as { value?: unknown; label?: unknown };
    // A labeled link has to show both parts in the option list — `label (url)`
    // — so neither the typed label nor the destination is dropped. Unlabeled
    // rows stay as just the URL.
    return label ? `${label} (${url ?? ''})` : String(url ?? '');
  }
  if (logicalType === 'ADDRESS') {
    return formatAddress(value as UfFieldValue);
  }
  if (logicalType === 'DURATION') {
    const { amount, period } = value as { amount?: unknown; period?: unknown };
    if (amount == null) return '';
    if (!intl) return `${amount} ${period}`;
    return formatDuration(intl, value as UfFieldValue) ?? '';
  }
  return String((value as { value?: unknown }).value ?? '');
};

export const getFieldOptionLabel = (
  option: FieldOption,
  logicalType?: string | null,
  formatNumber?: ReturnType<typeof useFormatNumber>,
  intl?: IntlShape,
  numberDisplay?: NumberDisplaySettings,
): string =>
  getOptionDisplayText(
    logicalType,
    option.attributes.value as OptionDraftValue,
    formatNumber,
    intl,
    numberDisplay,
  );

/** An existing option's value, parsed from its wire shape into `OptionDraftValue` — the compound-aware (URL/ADDRESS/MONETARY/DURATION) counterpart to `getFieldOptionLabel`'s scalar-only cast. */
export const getExistingOptionValue = (
  logicalType: string | null | undefined,
  option: FieldOption,
): OptionDraftValue =>
  parseOptionDraftValue(
    logicalType,
    option.attributes.value as Record<string, unknown>,
  );

/** Filters `existingOptions` down to what's still there after this session's staged deletions (or an empty list, once "delete all" is chosen). */
export const filterExistingOptions = (
  existingOptions: FieldOption[],
  deletedOptionIds: string[],
  deleteAllExistingOptions: boolean,
): FieldOption[] =>
  deleteAllExistingOptions
    ? []
    : existingOptions.filter((o) => !deletedOptionIds.includes(o.id));

const CSV_ROW_LIMIT = 10_000;
const CREATE_OPTIONS_CHUNK_SIZE = 2_000;

type CreateFieldOptionsTrigger = (arg: CreateFieldOptionsApiArg) => {
  unwrap: () => Promise<CreateFieldOptionsApiResponse>;
};

/**
 * Uploads field options in 2k-row chunks. All but the last chunk fire in
 * parallel (cache invalidation skipped); the last fires after to trigger
 * exactly one refetch.
 *
 * `defaultLabel`, when given, marks the matching item's `default: true` —
 * at most one label, so at most one item across the whole upload (and thus
 * per chunk/batch, which is all the API allows) ever claims it.
 */
export const createFieldOptionsInChunks = async (
  createFieldOptions: CreateFieldOptionsTrigger,
  optionSetId: string,
  values: OptionDraftValue[],
  defaultLabel?: string | null,
): Promise<void> => {
  if (values.length === 0) return;

  const chunks = chunk(values, CREATE_OPTIONS_CHUNK_SIZE);

  const toPayload = (c: OptionDraftValue[]) =>
    c.map((value) => ({
      type: 'field_options' as const,
      attributes: {
        option_set_id: optionSetId,
        value: typeof value === 'string' ? { value } : value,
        ...(defaultLabel != null &&
          toDefaultTrackingLabel(value) === defaultLabel && {
            default: true,
          }),
      },
    }));

  // All non-last chunks fire in parallel — no cache invalidation.
  if (chunks.length > 1) {
    const results = await Promise.allSettled(
      chunks.slice(0, -1).map((c) =>
        createFieldOptions({
          data: toPayload(c),
          skipInvalidation: true,
        }).unwrap(),
      ),
    );
    const firstFailure = results.find((r) => r.status === 'rejected') as
      | PromiseRejectedResult
      | undefined;
    if (firstFailure) throw firstFailure.reason;
  }

  // Last chunk fires after all others succeed and triggers the cache refetch.
  await createFieldOptions({
    data: toPayload(chunks[chunks.length - 1]),
  }).unwrap();
};

export type ParseCsvResult = { rows: string[]; truncated: boolean };

/** Thrown when a CSV is not valid UTF-8 (e.g. UTF-16 with a BOM). */
export class UnsupportedCsvEncodingError extends Error {
  constructor() {
    super('Unsupported CSV encoding');
    this.name = 'UnsupportedCsvEncodingError';
  }
}

const hasPrefix = (bytes: Uint8Array, prefix: readonly number[]): boolean =>
  bytes.length >= prefix.length && prefix.every((b, i) => bytes[i] === b);

/**
 * Decodes CSV bytes as UTF-8. UTF-8 with a BOM is accepted (Excel writes
 * one); UTF-16/32 BOMs, NUL bytes (typical of UTF-16 of ASCII), and any
 * other non-UTF-8 sequence are rejected so garbled content never reaches
 * preview or import.
 */
const decodeUtf8CsvText = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  if (
    hasPrefix(bytes, [0xff, 0xfe]) ||
    hasPrefix(bytes, [0xfe, 0xff]) ||
    hasPrefix(bytes, [0x00, 0x00, 0xfe, 0xff]) ||
    bytes.includes(0)
  ) {
    throw new UnsupportedCsvEncodingError();
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new UnsupportedCsvEncodingError();
  }
};

/**
 * Parses column A from a CSV file, capped at 10 000 non-empty rows (or
 * `maxRows`, when a smaller preview is requested — e.g. to peek at the first
 * few rows without stepping through the whole file). Drops a leading "Value"
 * header (matching the downloadable import template) so re-uploading it
 * doesn't create a literal "Value" option.
 */
export const parseCsvFile = async (
  file: File,
  maxRows: number = CSV_ROW_LIMIT,
): Promise<ParseCsvResult> => {
  const text = decodeUtf8CsvText(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const rows: string[] = [];
    let truncated = false;
    let isFirstRow = true;
    Papa.parse<string[]>(text, {
      skipEmptyLines: true,
      step: (result, parser) => {
        const value = result.data[0]?.trim();
        const isHeaderRow = isFirstRow && value?.toLowerCase() === 'value';
        isFirstRow = false;
        if (value && !isHeaderRow) rows.push(value);
        if (rows.length >= maxRows) {
          truncated = maxRows >= CSV_ROW_LIMIT;
          parser.abort();
        }
      },
      complete: () => resolve({ rows, truncated }),
      error: (err: Error) => reject(err),
    });
  });
};

/**
 * Returns csvLabels with duplicates removed, case-sensitively (matching the
 * backend's (option_set_id, value) uniqueness, which is a plain JSONB
 * equality check — "Test" and "test" are distinct, both-valid option
 * values), against both existingLabels and labels seen earlier in the CSV
 * itself. First occurrence wins.
 */
export const deduplicateCsvLabels = (
  csvLabels: string[],
  existingLabels: string[],
): string[] => {
  const seen = new Set(existingLabels);
  return csvLabels.filter((l) => {
    if (seen.has(l)) return false;
    seen.add(l);
    return true;
  });
};

/**
 * TEXT, EMAIL, NUMBER, and DATE fields support a single free-form Default
 * Value (and, on Preset options, marking one option as the set's default).
 * Deliberately narrower than `supportsOptionSetEditor` — a default value
 * needs a single well-defined scalar to store (`toDefaultValueAttribute`,
 * and the default-option tracking below, are both string-only); URL and
 * MONETARY's compound values have no established default-value wire
 * contract yet.
 */
export const supportsDefaultValue = (
  logicalType: string | null | undefined,
): boolean =>
  logicalType === 'TEXT' ||
  logicalType === 'EMAIL' ||
  logicalType === 'NUMBER' ||
  logicalType === 'DATE';

/** Reads a free-form TEXT field's saved `default_value` (`{ data: [{ value }] }`) back out as plain text, for seeding the Edit panel's Default Value input. */
export const getFieldDefaultValueText = (
  field: Pick<ClientField, 'attributes'> | null,
): string => {
  const data = field?.attributes.default_value?.data as
    | { value?: unknown }[]
    | undefined;
  const value = data?.[0]?.value;
  return typeof value === 'string' ? value : '';
};

/** Builds a free-form TEXT field's `default_value` attribute: the entered text, or explicit `null` to clear/omit it (a Preset options field's default comes from marking one of its options as the set's default instead). */
export const toDefaultValueAttribute = (
  text: string,
): { data: [{ value: string }] } | null => {
  const trimmed = text.trim();
  return trimmed ? { data: [{ value: trimmed }] } : null;
};

export type FieldCreationType =
  | 'TEXT'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'DATE'
  | 'MONETARY'
  | 'FILE'
  | 'USER'
  | 'EMAIL'
  | 'URL'
  | 'DURATION'
  | 'DEPARTMENT'
  | 'ADDRESS';

export type CreateFieldDraft = {
  custom_name: string;
  description: string;
  field_type: FieldCreationType;
  allow_multiple_values: boolean;
  allow_adding_new_options: boolean;
  default_value: string;
  option_set_id?: string;
  number_format?: NumberFormat;
  input_type?: TextInputType;
};

export const toCreateFieldPayload = ({
  custom_name,
  description,
  field_type,
  allow_multiple_values,
  allow_adding_new_options,
  default_value,
  option_set_id,
  number_format,
  input_type,
}: CreateFieldDraft) => {
  const base = {
    custom_name: custom_name.trim(),
    data_classification: 'PUBLIC' as const,
    ...(description.trim() && { description: description.trim() }),
    ...(option_set_id && { option_set_id }),
  };

  switch (field_type) {
    case 'TEXT':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'TEXT' as const,
          search_type: 'TEXT' as const,
          default_value: toDefaultValueAttribute(default_value),
          ...buildCustomSettingsAttributes(
            'TEXT',
            allow_multiple_values,
            allow_adding_new_options,
            undefined,
            undefined,
            option_set_id ? undefined : input_type,
          ),
        },
      };
    case 'FILE':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'FILE' as const,
          ...buildCustomSettingsAttributes(
            'FILE',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    case 'USER':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'USER' as const,
          ...buildCustomSettingsAttributes(
            'USER',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    case 'EMAIL':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'EMAIL' as const,
          search_type: 'TEXT' as const,
          default_value: toDefaultValueAttribute(default_value),
          ...buildCustomSettingsAttributes(
            'EMAIL',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    case 'URL':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'URL' as const,
          search_type: 'TEXT' as const,
          ...buildCustomSettingsAttributes(
            'URL',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    default:
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: field_type as LogicalType,
          ...(supportsDefaultValue(field_type) && {
            default_value: toDefaultValueAttribute(default_value),
          }),
          ...buildCustomSettingsAttributes(
            field_type,
            allow_multiple_values,
            allow_adding_new_options,
            undefined,
            number_format,
          ),
        },
      };
  }
};

// --- Preset options: which option is the set's default ---
//
// Tracked by label (unique across existing + new options, same guarantee CSV
// import already relies on via `deduplicateCsvLabels`) rather than id, since a
// newly-added option has no id until it's actually created. Shared by
// AddFieldPanel (create) and EditFieldPanel (edit) so both panels' option
// list handlers (delete/edit/bulk-delete/toggle) apply the same rule.

/**
 * Extracts the label an `OptionDraftValue` participates in default-option
 * tracking under — a plain string is its own label. A compound value (URL,
 * MONETARY, ADDRESS, DURATION) has no natural string identity, so it's
 * canonicalized to a JSON string with keys sorted: two values built from the
 * same fields in a different order (e.g. a fresh add vs. one round-tripped
 * through an edit) still compare equal.
 */
export const toDefaultTrackingLabel = (value: OptionDraftValue): string =>
  typeof value === 'string'
    ? value
    : JSON.stringify(value, Object.keys(value).sort());

/** Clears `currentDefault` if `removedLabel` is the option being removed. */
export const clearDefaultOptionIfRemoved = (
  currentDefault: string | null,
  removedLabel: string | undefined,
): string | null =>
  removedLabel != null && removedLabel === currentDefault
    ? null
    : currentDefault;

/** Clears `currentDefault` if it's among the options being bulk-removed. */
export const clearDefaultOptionIfAnyRemoved = (
  currentDefault: string | null,
  removedLabels: (string | undefined)[],
): string | null =>
  currentDefault != null && removedLabels.includes(currentDefault)
    ? null
    : currentDefault;

/** Carries `currentDefault` over to `newLabel` when the option it pointed at (`oldLabel`) was just renamed. */
export const renameDefaultOption = (
  currentDefault: string | null,
  oldLabel: string | undefined,
  newLabel: string,
): string | null =>
  oldLabel != null && oldLabel === currentDefault ? newLabel : currentDefault;

/** Toggles `label`'s default status: default if it wasn't, cleared if it already was. */
export const toggleDefaultOption = (
  currentDefault: string | null,
  label: string,
): string | null => (label === currentDefault ? null : label);

/** Reads an option set's current default (if any) off a loaded page of its field options. */
export const getServerDefaultOption = (
  options: FieldOption[],
  logicalType?: string | null,
  formatNumber?: ReturnType<typeof useFormatNumber>,
  intl?: IntlShape,
): { id: string; label: string } | null => {
  const serverDefault = options.find((o) => o.attributes.default);
  return serverDefault
    ? {
        id: serverDefault.id,
        label: getFieldOptionLabel(
          serverDefault,
          logicalType,
          formatNumber,
          intl,
        ),
      }
    : null;
};

export type DefaultOptionAction =
  | { type: 'none' }
  | { type: 'promote'; optionId: string }
  | { type: 'demote'; optionId: string };

/**
 * Decides what (if anything) needs to change server-side for an option set's
 * default, once only *existing* options are in play — a default that
 * resolves to one of `optionsToCreate` needs no separate action here: it's
 * sent as `default: true` on creation instead, which the backend already
 * treats as demoting whatever was previously default.
 *
 * `existingDefaultOptionId` is the id of whichever *existing* option
 * `defaultOption` currently refers to (or null if it refers to a
 * not-yet-created option, or nothing) — tracked directly by the caller as
 * the user picks/renames/removes options, rather than looked up here by
 * label against a loaded page of options. Option sets can hold tens of
 * thousands of options, so the panel only ever loads a search-scoped page of
 * them; looking the label up against that page here would silently produce
 * the wrong answer (e.g. a false "not found") whenever a search filter
 * happens to be active at submit time.
 */
export const resolveDefaultOptionAction = ({
  defaultOption,
  originalDefaultOptionId,
  existingDefaultOptionId,
  deletedOptionIds,
  optionsToCreate,
}: {
  defaultOption: string | null;
  originalDefaultOptionId: string | null;
  existingDefaultOptionId: string | null;
  deletedOptionIds: string[];
  optionsToCreate: string[];
}): DefaultOptionAction => {
  if (defaultOption != null && optionsToCreate.includes(defaultOption)) {
    return { type: 'none' };
  }

  const deleted = new Set(deletedOptionIds);
  const newDefaultId =
    existingDefaultOptionId && !deleted.has(existingDefaultOptionId)
      ? existingDefaultOptionId
      : null;

  if (newDefaultId && newDefaultId !== originalDefaultOptionId) {
    return { type: 'promote', optionId: newDefaultId };
  }
  if (
    !newDefaultId &&
    originalDefaultOptionId &&
    !deleted.has(originalDefaultOptionId)
  ) {
    return { type: 'demote', optionId: originalDefaultOptionId };
  }
  return { type: 'none' };
};

export const buildFormFooterActions = ({
  cancelLabel,
  submitLabel,
  isSubmitting,
  disabled = false,
  tooltip,
  onCancel,
  onSubmit,
}: {
  cancelLabel: string;
  submitLabel: string;
  isSubmitting: boolean;
  /** Additional condition (beyond `isSubmitting`) disabling the primary action — e.g. an incomplete preset-options field. */
  disabled?: boolean;
  tooltip?: string;
  onCancel: () => void;
  onSubmit: () => void;
}): Action[] => [
  {
    label: cancelLabel,
    text: cancelLabel,
    variant: 'tertiary',
    onClick: onCancel,
  },
  {
    label: submitLabel,
    text: submitLabel,
    variant: 'primary',
    isLoading: isSubmitting,
    disabled: isSubmitting || disabled,
    tooltip: tooltip ? { text: tooltip } : undefined,
    onClick: onSubmit,
  },
];
